import React, { useState, useEffect, useCallback, useMemo } from "react";
import Handlebars from "handlebars";

// Default test data that matches the SendReminder service data structure
const getDefaultTestData = (organizationName, fundName, supportEmail, message) => ({
  first_name: "John",
  last_name: "Doe",
  full_name: "John Doe",
  organization_name: organizationName || "Sample Organization",
  support_email: supportEmail || "support@example.com",
  fund_name: fundName || "Membership Dues",
  fund_id: "123",
  fund_user_id: "456",
  invoice_id: "789",
  amount_due: "$50.00",
  due_date: "January 15th, 2026",
  payment_link: "https://unionly.io/profile",
  logo_url: "http://cdn.mcauto-images-production.sendgrid.net/fc0a5f8d6c9b25c4/ac417efd-5f84-443c-a0e7-24ecf58bb273/615x342.png",
  invoice_status: "unpaid",
  // Custom content fields
  use_custom_content: false,
  message: message || "",
  custom_subject: "",
  has_attachment: false,
  attachment_url: "",
  attachment_filename: "",
  current_year: new Date().getFullYear(),
});

const TestDataForm = ({ testData, onChange, isCollapsed, onToggleCollapse }) => {
  const handleChange = (field, value) => {
    onChange({ ...testData, [field]: value });
  };

  const fields = [
    { key: "first_name", label: "First Name", type: "text" },
    { key: "last_name", label: "Last Name", type: "text" },
    { key: "organization_name", label: "Organization Name", type: "text" },
    { key: "fund_name", label: "Fund Name", type: "text" },
    { key: "amount_due", label: "Amount Due", type: "text" },
    { key: "due_date", label: "Due Date", type: "text" },
    { key: "invoice_status", label: "Invoice Status", type: "select", options: ["unpaid", "paid", "partial_payment", "failed"] },
    { key: "support_email", label: "Support Email", type: "text" },
  ];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggleCollapse}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">Test Data</span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!isCollapsed && (
        <div className="p-4 bg-white">
          <p className="text-xs text-gray-500 mb-4">
            Edit these values to see how the email will look with different data.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {fields.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {field.label}
                </label>
                {field.type === "select" ? (
                  <select
                    value={testData[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  >
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={testData[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewFrame = ({ html, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading template...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg border border-red-200">
        <div className="text-center px-6">
          <svg className="w-10 h-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-sm font-medium text-red-800">Failed to load template</p>
          <p className="text-xs text-red-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!html) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-center">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-sm text-gray-500">No preview available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <iframe
        srcDoc={html}
        title="Email Preview"
        className="w-full h-[600px] border-0"
        sandbox="allow-same-origin"
      />
    </div>
  );
};

const EmailTemplatePreview = ({
  fetchTemplateUrl,
  reminderType, // 'invoice' or 'overdue' - can change dynamically
  organizationName,
  fundName,
  supportEmail,
  initialMessage,
  messageFieldId,
  message, // Direct message prop (for when used inside form component)
  customSubject, // Custom subject line
  useCustomContent, // Whether to use custom content
  hasAttachment, // Whether there's an attachment
  attachmentFilename, // Name of the attachment file
}) => {
  const [templateHtml, setTemplateHtml] = useState(null);
  const [compiledTemplate, setCompiledTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentType, setCurrentType] = useState(reminderType);
  const [testData, setTestData] = useState(() =>
    getDefaultTestData(organizationName, fundName, supportEmail, initialMessage || message)
  );
  const [isTestDataCollapsed, setIsTestDataCollapsed] = useState(true);

  // Update test data when message, custom content, or attachment props change (from parent form)
  useEffect(() => {
    setTestData((prev) => ({
      ...prev,
      message: message || '',
      custom_subject: customSubject || '',
      use_custom_content: useCustomContent || false,
      has_attachment: hasAttachment || false,
      attachment_filename: attachmentFilename || '',
      attachment_url: hasAttachment ? '#' : '',
    }));
  }, [message, customSubject, useCustomContent, hasAttachment, attachmentFilename]);

  // Fetch template on mount and when reminderType changes
  useEffect(() => {
    const fetchTemplate = async () => {
      setIsLoading(true);
      setError(null);
      setCurrentType(reminderType);

      // Construct URL with type parameter
      const url = new URL(fetchTemplateUrl, window.location.origin);
      if (reminderType) {
        url.searchParams.set('type', reminderType);
      }

      try {
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          },
        });

        const data = await response.json();

        if (data.success && data.html) {
          setTemplateHtml(data.html);
          // Compile the template once
          try {
            const compiled = Handlebars.compile(data.html);
            setCompiledTemplate(() => compiled);
          } catch (compileError) {
            console.error("Handlebars compile error:", compileError);
            setError("Failed to compile template");
          }
        } else {
          setError(data.error || "Failed to fetch template");
        }
      } catch (fetchError) {
        console.error("Fetch error:", fetchError);
        setError("Network error: " + fetchError.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (fetchTemplateUrl) {
      fetchTemplate();
    }
  }, [fetchTemplateUrl, reminderType]);

  // Sync message from form field
  useEffect(() => {
    if (!messageFieldId) return;

    const messageField = document.getElementById(messageFieldId);
    if (!messageField) return;

    const handleMessageChange = () => {
      setTestData((prev) => ({ ...prev, message: messageField.value }));
    };

    // Set initial value
    if (messageField.value) {
      setTestData((prev) => ({ ...prev, message: messageField.value }));
    }

    messageField.addEventListener("input", handleMessageChange);
    return () => messageField.removeEventListener("input", handleMessageChange);
  }, [messageFieldId]);

  // Render the template with test data
  const renderedHtml = useMemo(() => {
    if (!compiledTemplate) return null;

    try {
      // Update full_name when first/last name change
      const dataWithFullName = {
        ...testData,
        full_name: `${testData.first_name} ${testData.last_name}`,
      };
      return compiledTemplate(dataWithFullName);
    } catch (renderError) {
      console.error("Handlebars render error:", renderError);
      return null;
    }
  }, [compiledTemplate, testData]);

  return (
    <div className="space-y-4">
      <TestDataForm
        testData={testData}
        onChange={setTestData}
        isCollapsed={isTestDataCollapsed}
        onToggleCollapse={() => setIsTestDataCollapsed(!isTestDataCollapsed)}
      />
      <PreviewFrame html={renderedHtml} isLoading={isLoading} error={error} />
    </div>
  );
};

export default EmailTemplatePreview;
